/** Must match backend `attachment-upload.validation.ts` defaults. */
export const MAX_ATTACHMENT_BYTES = 78643200;

export const ATTACHMENT_MSG_TOO_LARGE = 'File is too large. Maximum upload size is 75 MB.';
export const ATTACHMENT_MSG_UNSUPPORTED_MIME =
  'Unsupported file type. Allowed files include images, PDF, documents, spreadsheets, text/CSV, and MP4/MOV/WebM videos.';

const EXT_TO_MIME: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.zip': 'application/zip',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export const ALLOWED_ATTACHMENT_MIME_TYPES: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'application/json',
  'application/zip',
  'application/x-zip-compressed',
  'video/mp4',
  'video/webm',
  'video/quicktime',
];

const ALLOWED_SET = new Set(ALLOWED_ATTACHMENT_MIME_TYPES.map((m) => m.toLowerCase()));

function inferMimeFromName(fileName: string): string {
  const lower = fileName.toLowerCase();
  const dot = lower.lastIndexOf('.');
  if (dot < 0) return '';
  return EXT_TO_MIME[lower.slice(dot)] ?? '';
}

/** MIME used for client-side allowlist checks (browser `File.type` or extension fallback). */
export function resolveClientAttachmentMime(file: File): string {
  const t = file.type?.trim().toLowerCase();
  if (t) return t;
  return inferMimeFromName(file.name);
}

export function validateAttachmentFileClient(file: File): string | null {
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return ATTACHMENT_MSG_TOO_LARGE;
  }
  const mime = resolveClientAttachmentMime(file);
  if (!mime || !ALLOWED_SET.has(mime)) {
    return ATTACHMENT_MSG_UNSUPPORTED_MIME;
  }
  return null;
}

/** `accept` for `<input type="file">` — MIME tokens plus common extensions. */
export const ATTACHMENT_FILE_INPUT_ACCEPT = [
  ...ALLOWED_ATTACHMENT_MIME_TYPES,
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.pdf',
  '.txt',
  '.csv',
  '.json',
  '.zip',
  '.mp4',
  '.webm',
  '.mov',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
].join(',');
